import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom"
import { formatDateToRelativeOrShortDate } from "../Helpers/utils";
import PostFooter from "./PostFooter";
import PostOptions from "./PostOptions";
import { useGlobalState } from "../contexts/GlobalStateContext";
import { ApproveButton } from "./ApproveButton";
import { DisapproveButton } from "./DisapproveButton";
import { EscalateButton } from "./EscalateButton";
import ShareModal from "./ShareModal";

const Post = ({ post, pageName, onImageClick }) => {
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
  const assetBaseUrl = post.assetBaseUrl || baseUrl;
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    globalState,
    setShowLogins,
    setMoreInfoPost,
    setSelectedConvo,
    setShowNotifications,
    setShowLink,
    setJustPosted,
    setShowUserInfo
  } = useGlobalState();
  const [blurValue, setBlurValue] = useState(post.blur ?? 0);
  const increaseBlur = () => setBlurValue((prev) => prev + 1);
  const decreaseBlur = () => setBlurValue((prev) => Math.max(0, prev - 1));
  const [selectedUuid, setSelectedUuid] = useState(null);

  const handleShowOptions = () => {
    if (globalState.isAuthenticated) {
      setShowOptions(true);
    } else {
      setShowLogins(true);
    }
  };
  const handleShowProfile = (username) => {
    setShowNotifications(false);
    setSelectedConvo(null);
    setJustPosted(null)
    navigate(`/@${encodeURIComponent(username)}`);
  };

  const handleContextContainerClick = (post) => {
    setShowNotifications(false);
    setSelectedConvo(null);
    setJustPosted(null)
    navigate(`/context?query=${encodeURIComponent(post.context)}`);
  };

  const handleAbbreviationClick = () => {
    setShowNotifications(false);
    setSelectedConvo(null);
    setJustPosted(null)
    navigate(`/search?query=:${post.abbreviation}`);
  };


  const handleContainerClick = (e) => {
    // ignore interactive elements
    if (
      e.target.closest("button") ||
      e.target.closest("a") ||
      e.target.closest(".dp") ||
      e.target.closest(".link")
    ) return;

    if (!isExpanded) {
      setIsExpanded(true);
    }
  };

  const handleImageClick = (e) => {
    e.stopPropagation(); // 🔥 THIS is the key

    if (!isExpanded) {
      setIsExpanded(true); // optional: expand on first image click
      return;
    }

    if (onImageClick) onImageClick(post);
  };


  const formatUsername = (username) => {
    if (!username) return "";

    const parts = username.split("-");
    if (parts.length < 3) return username;

    const [campRaw, sectorRaw, hutRaw] = parts;

    const camp = (
      campRaw.charAt(0).toUpperCase() +
      campRaw.slice(1).toLowerCase()
    ).replace(/(?<=\D)(?=\d)/g, " ");

    const sector = sectorRaw
      .toUpperCase()
      .replace(/^([A-Z])(\d+).*/, "$1$2");

    const hut = hutRaw.replace(/\D/g, "");

    return `${camp} ${sector} ${hut}`;
  };

  useEffect(() => {
    if (globalState.exitedPost?.uuid === post.uuid) {
      setIsExpanded(true);
    }
  }, [globalState.exitedPost, post.uuid]);

  return (
    <>
      {selectedUuid && (
        <ShareModal uuid={selectedUuid} onClose={() => setSelectedUuid(null)} />
      )}
      <div onClick={handleContainerClick}>
        {(post.reported || post.escalated) && (
          <>
            <button id="minus-blur" onClick={decreaseBlur}>
              - Blur
            </button>
            <button id="add-blur" onClick={increaseBlur}>
              + Blur
            </button>
          </>
        )}
        <div className={`post post-${post.uuid}`}>
          <div className="image-wrapper">
            {!imageLoaded && <div className="image-placeholder" />}

            <img
              key={`${pageName}-${post.uuid}`}
              src={`${assetBaseUrl}/thumbnails/${post.uuid || "default_thumbnail"}.jpg`}
              className="thumbnail"
              style={{
                display: imageLoaded ? "block" : "none",
                filter: imageLoaded ? `blur(${blurValue}px)` : "none",
                transition: "filter 0.3s ease-in-out",
              }}
              onLoad={() => setImageLoaded(true)}
              onError={(e) => {
                e.target.src = `${assetBaseUrl}/thumbnails/default_thumbnail.jpg`;
                e.target.src = `${baseUrl}/thumbnails/default_thumbnail.jpg`;
                setImageLoaded(true);
              }}
              alt="Post Thumbnail"
              // The prop is named 'onImageClick', so we use that here.
              onClick={handleImageClick}
            />


            {/* Only show the container once the image is loaded */}
            {imageLoaded && isExpanded && (
              <div>
                <div className="top-left" style={{ cursor: "pointer" }}>
                  {" "}
                  {/* Sanitize username */}
                  <img
                    onClick={() => { setShowUserInfo(true); handleShowProfile(post.username) }}
                    className="dp"
                    src={`${assetBaseUrl}/profile_pics_icons/${post.user_id}.jpg?v=${post.avatar_version}`}
                    onError={(e) => {
                      e.target.src = `${assetBaseUrl}/thumbnails/default_thumbnail.jpg`;
                      setImageLoaded(true);
                    }}
                    alt="User Avatar"
                  />{" "}
                  {/* Sanitize username */}
                  <span
                    onClick={() => handleShowProfile(post.username)}
                    id="username" style={{ cursor: "pointer", color: post.text_bg, backgroundColor: post.theme, padding: '0 5px' }}
                  >
                    {formatUsername(post.username)}
                  </span>{" "}
                  {/* Sanitize username */}
                </div>

                <div
                  className="top-right"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px', // optional spacing between items
                  }}
                >
                  <span id="date" style={{ color: post.text_bg, backgroundColor: post.theme }}>
                    {formatDateToRelativeOrShortDate(post.date)}
                  </span>
                  <div
                    style={{
                      backgroundColor: post.theme,
                      borderRadius: '9999px',
                      padding: '3.5px 0px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: post.text_bg,
                    }}
                    onClick={handleShowOptions}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="1"></circle>
                      <circle cx="12" cy="5" r="1"></circle>
                      <circle cx="12" cy="19" r="1"></circle>
                    </svg>
                  </div>
                </div>
                {showOptions && (
                  <PostOptions
                    post={post}
                    closeOptions={() => { setShowOptions(false) }}
                  />
                )}
                {post.context && (
                  <div
                    className="context-container"
                    onClick={() => handleContextContainerClick(post)}
                  >
                    <svg height="20" width="20">
                      <polygon
                        points="0,0 7,10 0,20 20,20 20,0"
                        style={{ fill: post.theme }}
                      />
                    </svg>
                    <span
                      className="context"
                      style={{ color: post.text_bg, backgroundColor: post.theme }}
                    >
                      {post.context}
                    </span>
                    <svg height="20" width="20">
                      <polygon
                        points="0,0 0,20 20,20 13,10 20,0"
                        style={{ fill: post.theme }}
                      />
                    </svg>
                  </div>
                )}
                <div
                  className={
                    post.abbreviation ? "centered" : "centered_without_abvri"
                  }
                  style={{ color: post.theme }}
                >
                  {post.abbreviation && (
                    <>
                      <span
                        className={`abvri abvri${post.post_class}`}
                        style={{ cursor: "pointer" }}
                        onClick={handleAbbreviationClick}
                      >
                        {post.abbreviation} {/* Sanitize abbreviation */}
                      </span>
                    </>
                  )}
                  <span
                    className={`fullForm fullForm${post.post_class}`}
                    style={{ backgroundColor: post.text_bg, cursor: "pointer" }}
                    onClick={() => setMoreInfoPost(post)}
                  >
                    {post.full_form} {/* Sanitize full form */}
                  </span>
                </div>

                {post.link && (
                  <span
                    className="link"
                    id="draft_link"
                    onClick={() => setShowLink(post.link)}
                    role="button"
                  >
                    &#128279;
                  </span>
                )}

                {post.reported ? (
                  <EscalateButton post={post} />
                ) : (
                  <PostFooter post={post} />
                )}

                {post.reported || post.escalated ? (
                  <>
                    <ApproveButton
                      post={post}
                      blurValue={blurValue}
                      escalated={post.escalated}
                    />
                    <DisapproveButton post={post} escalated={post.escalated} />
                  </>
                ) : (
                  <>
                    {!post.draft && (
                      <div className="bottom-right" style={{ color: post.text_bg, backgroundColor: post.theme, cursor: "pointer" }}>
                        <span
                          id="s"
                          alt="Share"
                          onClick={() => setSelectedUuid(post.uuid)}
                        >
                          Share
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Post;
